// Experience, levels and skill trees on the ability system (decision 0050: "fights grant experience, levels raise
// stats, each level gives a point for a small skill tree per weapon"). All of it is data the game passes in: the
// level curve, the growth per level, the points per level, the trees and their nodes, the experience each source is
// worth. The state is plain JSON the game keeps (a kernel component), so saves and hashes cover it.
//
// How it reaches the numbers: the progress state is the source of truth, and `sync` makes a character's ability state
// carry what it implies as infinite effects owned by "progress": one level effect whose stacks are the levels gained
// (each stack adds the growth), and one effect per bought node (its modifiers and granted tags). A fighter re-created
// from its base values gets them back on the next sync, and nothing is ever written into a base twice.
// A node can also swap one move or ability for another (`swaps`: an upgraded skill, a combo that unlocks a fourth
// hit); the game asks `swaps(state)` when it starts a move. Its granted tags unlock whatever the game gates on them.
import type { AbilitySet, AbilityState, EffectDef, Modifier } from "./system";
import type { Tag } from "./tags";

/** How much experience each level takes. Either a table (`table[i]`: from level i + 1 to i + 2) or a power curve:
 *  round(base × level ^ exponent) from `level` to `level + 1`. */
export interface LevelCurve {
  /** The highest level (≥ 1). */
  maxLevel: number;
  table?: readonly number[];
  base?: number;
  exponent?: number;
}

/** One node of a skill tree. */
export interface SkillNode {
  id: string;
  /** Skill points it costs (≥ 1). */
  cost: number;
  /** Nodes of the same tree bought first. */
  requires?: readonly string[];
  /** The character's level must be at least this. */
  minLevel?: number;
  /** Modifiers it adds while owned (an infinite effect). */
  modifiers?: readonly Modifier[];
  /** Tags the character has while it is owned (an unlock the game gates on). */
  grants?: readonly Tag[];
  /** Moves or abilities it swaps for others while owned (`from` → `to`): an upgraded skill. */
  swaps?: Readonly<Record<string, string>>;
}

/** A skill tree (one per weapon in spike 002). */
export interface SkillTree {
  id: string;
  nodes: readonly SkillNode[];
}

/** The game's progression data. */
export interface ProgressionDef {
  curve: LevelCurve;
  /** What each level above the first adds to the current values (attribute → amount per level). */
  growth: Readonly<Record<string, number>>;
  /** Skill points each level gained gives. */
  pointsPerLevel: number;
  trees: readonly SkillTree[];
  /** Experience by source (an enemy kind, a quest): `reward(source)`. */
  rewards?: Readonly<Record<string, number>>;
}

/** A character's progress. Plain JSON: save it, hash it. */
export interface ProgressState {
  level: number;
  /** Experience toward the next level. */
  xp: number;
  /** Experience earned in all. */
  total: number;
  /** Unspent skill points. */
  points: number;
  /** Bought nodes by tree, in the order bought. */
  nodes: Record<string, string[]>;
}

/** Why a node can't be bought. */
export type BuyRefusal = "unknown" | "owned" | "points" | "requires" | "level";

/** What a grant did. */
export interface Granted {
  /** Experience actually added (0 at the top level). */
  added: number;
  /** Each level reached, in order. */
  levels: number[];
}

/** The owner id of the effects progression puts on a character. */
export const PROGRESS_OWNER = "progress";
/** The level effect's id. */
export const LEVEL_EFFECT = `${PROGRESS_OWNER}:level`;
/** A bought node's effect id. */
export const nodeEffectId = (tree: string, node: string): string => `${PROGRESS_OWNER}:${tree}.${node}`;

/** Experience from `level` to `level + 1` (Infinity at the top). */
export function xpToNext(curve: LevelCurve, level: number): number {
  if (level >= curve.maxLevel) return Infinity;
  if (curve.table) return curve.table[level - 1] ?? Infinity;
  return Math.max(1, Math.round((curve.base ?? 1) * level ** (curve.exponent ?? 1)));
}

/** Problems with a progression definition (empty when sound). `attributes`: the stat set's ids, when known. */
export function checkProgression(def: ProgressionDef, attributes?: readonly string[]): string[] {
  const out: string[] = [];
  const c = def.curve;
  if (!Number.isInteger(c.maxLevel) || c.maxLevel < 1) out.push(`curve: maxLevel ${c.maxLevel} is not a whole number ≥ 1`);
  if (c.table) {
    if (c.table.length < c.maxLevel - 1) out.push(`curve: the table has ${c.table.length} steps for ${c.maxLevel} levels`);
    c.table.forEach((v, i) => {
      if (!(v > 0)) out.push(`curve: step ${i + 1} is ${v}`);
      if (i > 0 && v < c.table![i - 1]!) out.push(`curve: step ${i + 1} (${v}) is below step ${i} (${c.table![i - 1]})`);
    });
  } else if (!(Number(c.base) > 0) || !(Number(c.exponent ?? 1) >= 0)) out.push("curve: a power curve needs base > 0 and exponent ≥ 0");
  if (!Number.isInteger(def.pointsPerLevel) || def.pointsPerLevel < 0) out.push(`pointsPerLevel ${def.pointsPerLevel} is not a whole number ≥ 0`);
  const known = attributes ? new Set(attributes) : null;
  for (const [a, v] of Object.entries(def.growth)) {
    if (known && !known.has(a)) out.push(`growth names unknown stat "${a}"`);
    if (!Number.isFinite(v)) out.push(`growth of "${a}" is ${v}`);
  }
  for (const [s, v] of Object.entries(def.rewards ?? {})) if (!(v >= 0)) out.push(`reward "${s}" is ${v}`);
  const trees = new Set<string>();
  for (const t of def.trees) {
    if (trees.has(t.id)) out.push(`tree "${t.id}" is defined twice`);
    trees.add(t.id);
    const ids = new Set(t.nodes.map((n) => n.id));
    if (ids.size !== t.nodes.length) out.push(`tree "${t.id}" has a node id twice`);
    for (const n of t.nodes) {
      const at = `tree "${t.id}" node "${n.id}"`;
      if (!Number.isInteger(n.cost) || n.cost < 1) out.push(`${at}: cost ${n.cost}`);
      for (const r of n.requires ?? []) if (!ids.has(r)) out.push(`${at} requires unknown node "${r}"`);
      for (const m of n.modifiers ?? []) if (known && !known.has(m.attribute)) out.push(`${at} modifies unknown stat "${m.attribute}"`);
      if (n.minLevel !== undefined && n.minLevel > c.maxLevel) out.push(`${at} needs level ${n.minLevel}, above the top (${c.maxLevel})`);
    }
    // requirements must not loop (each node reachable from the roots)
    const done = new Set<string>();
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of t.nodes) if (!done.has(n.id) && (n.requires ?? []).every((r) => done.has(r))) (done.add(n.id), (grew = true));
    }
    for (const n of t.nodes) if (!done.has(n.id) && (n.requires ?? []).every((r) => ids.has(r))) out.push(`tree "${t.id}" node "${n.id}" is in a requirement loop`);
  }
  return out;
}

/** Progression over the game's definition. */
export interface Progression {
  readonly def: ProgressionDef;
  /** A fresh state at level 1 (or `level`, with the points it would have earned). */
  init(level?: number): ProgressState;
  xpToNext(level: number): number;
  /** Experience a source is worth (0 when the table doesn't name it). */
  reward(source: string): number;
  /** Add experience: levels gained on the way (each gives its points); stops at the top level. */
  grant(s: ProgressState, amount: number): Granted;
  canBuy(s: ProgressState, tree: string, node: string): BuyRefusal | null;
  /** Buy a node (spends its points). Null on success, else why not (nothing changed). */
  buy(s: ProgressState, tree: string, node: string): BuyRefusal | null;
  owned(s: ProgressState, tree: string, node: string): boolean;
  /** Every tag the bought nodes grant. */
  tags(s: ProgressState): Tag[];
  /** The moves or abilities swapped by bought nodes (later purchases win). */
  swaps(s: ProgressState): Map<string, string>;
  /** The effects the state implies, as `AbilitySet.sync` takes them. */
  effects(s: ProgressState): { def: EffectDef; stacks: number }[];
  /** Make a character's ability state carry exactly what the progress implies. */
  sync(set: AbilitySet, stats: AbilityState, s: ProgressState): void;
  /** Problems with a state against the definition (unknown nodes, missing requirements, points that don't add up). */
  check(s: ProgressState): string[];
}

/** Build progression over a definition. Throws listing every problem `checkProgression` finds. */
export function createProgression(def: ProgressionDef, attributes?: readonly string[]): Progression {
  const issues = checkProgression(def, attributes);
  if (issues.length) throw new Error(`progression: ${issues.join("; ")}`);
  const trees = new Map(def.trees.map((t) => [t.id, { tree: t, nodes: new Map(t.nodes.map((n) => [n.id, n])) }]));
  const node = (tree: string, id: string): SkillNode | undefined => trees.get(tree)?.nodes.get(id);
  // the level effect: each stack (a level gained) adds the growth
  const level: EffectDef = {
    id: LEVEL_EFFECT,
    duration: "infinite",
    modifiers: Object.entries(def.growth).map(([attribute, value]) => ({ attribute, op: "add" as const, value })),
    stacking: { limit: Math.max(1, def.curve.maxLevel - 1) },
  };
  // one definition object per node, made once (sync compares by identity)
  const nodeDefs = new Map<string, EffectDef>();
  for (const t of def.trees)
    for (const n of t.nodes) {
      const id = nodeEffectId(t.id, n.id);
      nodeDefs.set(id, { id, duration: "infinite", ...(n.modifiers?.length ? { modifiers: n.modifiers } : {}), ...(n.grants?.length ? { grants: n.grants } : {}) });
    }
  const spentOn = (s: ProgressState): number => Object.entries(s.nodes).reduce((sum, [t, ns]) => sum + ns.reduce((a, n) => a + (node(t, n)?.cost ?? 0), 0), 0);

  const p: Progression = {
    def,
    init(lv = 1) {
      const l = Math.max(1, Math.min(def.curve.maxLevel, Math.floor(lv)));
      return { level: l, xp: 0, total: 0, points: (l - 1) * def.pointsPerLevel, nodes: {} };
    },
    xpToNext: (l) => xpToNext(def.curve, l),
    reward: (src) => def.rewards?.[src] ?? 0,
    grant(s, amount) {
      if (!(amount >= 0) || !Number.isFinite(amount)) throw new Error(`progression: experience ${amount} is not a finite amount ≥ 0`);
      const out: Granted = { added: 0, levels: [] };
      let left = amount;
      while (left > 0 && s.level < def.curve.maxLevel) {
        const need = xpToNext(def.curve, s.level) - s.xp;
        if (left < need) {
          s.xp += left;
          out.added += left;
          left = 0;
          break;
        }
        left -= need;
        out.added += need;
        s.level++;
        s.xp = 0;
        s.points += def.pointsPerLevel;
        out.levels.push(s.level);
      }
      s.total += out.added;
      return out;
    },
    canBuy(s, tree, id) {
      const n = node(tree, id);
      if (!n) return "unknown";
      if ((s.nodes[tree] ?? []).includes(id)) return "owned";
      if ((n.requires ?? []).some((r) => !(s.nodes[tree] ?? []).includes(r))) return "requires";
      if (n.minLevel !== undefined && s.level < n.minLevel) return "level";
      if (s.points < n.cost) return "points";
      return null;
    },
    buy(s, tree, id) {
      const why = p.canBuy(s, tree, id);
      if (why) return why;
      s.points -= node(tree, id)!.cost;
      (s.nodes[tree] ??= []).push(id);
      return null;
    },
    owned: (s, tree, id) => (s.nodes[tree] ?? []).includes(id),
    tags(s) {
      const out = new Set<Tag>();
      for (const [t, ns] of Object.entries(s.nodes)) for (const n of ns) for (const g of node(t, n)?.grants ?? []) out.add(g);
      return [...out].sort();
    },
    swaps(s) {
      const out = new Map<string, string>();
      for (const [t, ns] of Object.entries(s.nodes)) for (const n of ns) for (const [from, to] of Object.entries(node(t, n)?.swaps ?? {})) out.set(from, to);
      return out;
    },
    effects(s) {
      const out: { def: EffectDef; stacks: number }[] = [];
      if (s.level > 1 && Object.keys(def.growth).length) out.push({ def: level, stacks: s.level - 1 });
      for (const [t, ns] of Object.entries(s.nodes))
        for (const n of ns) {
          const d = nodeDefs.get(nodeEffectId(t, n));
          if (d && (d.modifiers || d.grants)) out.push({ def: d, stacks: 1 });
        }
      return out;
    },
    sync: (set, stats, s) => set.sync(stats, PROGRESS_OWNER, p.effects(s)),
    check(s) {
      const out: string[] = [];
      if (!Number.isInteger(s.level) || s.level < 1 || s.level > def.curve.maxLevel) out.push(`level ${s.level} outside 1..${def.curve.maxLevel}`);
      if (!(s.xp >= 0) || (s.level < def.curve.maxLevel && s.xp >= xpToNext(def.curve, s.level))) out.push(`xp ${s.xp} at level ${s.level} (next at ${xpToNext(def.curve, s.level)})`);
      if (!Number.isInteger(s.points) || s.points < 0) out.push(`points ${s.points}`);
      for (const [t, ns] of Object.entries(s.nodes))
        ns.forEach((n, i) => {
          const d = node(t, n);
          if (!d) return void out.push(`unknown node "${t}.${n}"`);
          for (const r of d.requires ?? []) if (!ns.slice(0, i).includes(r)) out.push(`node "${t}.${n}" bought before its requirement "${r}"`);
        });
      const earned = (s.level - 1) * def.pointsPerLevel;
      if (s.points + spentOn(s) !== earned) out.push(`points ${s.points} + spent ${spentOn(s)} ≠ earned ${earned}`);
      return out;
    },
  };
  return p;
}
